import React, { FC, useEffect, useMemo, useState } from 'react'
import { Button, Dropdown, Form } from 'react-bootstrap'
import { FiSettings } from 'react-icons/fi'
import { FaArrowRight, FaArrowTrendDown, FaArrowTrendUp } from 'react-icons/fa6'
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { formatNumber, formatNumberLocale } from '../../../_utils'
import { HourlyClickRow } from './types'

const dashboardTableBlue = '#5b9bd5'
const automatedLineColor = '#9fc5e8'
const commissionLineColor = '#6f42c1'
const pageViewsLineColor = '#6c757d'
const ctrLineColor = '#0f766e'
const epcLineColor = '#d97706'
const conversionsLineColor = '#db2777'
const conversionRateLineColor = '#7c3aed'
const rpmLineColor = '#475569'
const inProgressLineOpacity = 0.35
const metricValueKeys = ['totalClicks', 'automatedClicks', 'commission', 'pageViews', 'ctr', 'epc', 'conversions', 'conversionRate', 'rpm'] as const
const tooltipMetricOrder = ['totalClicks', 'automatedClicks', 'pageViews', 'commission', 'ctr', 'epc', 'conversions', 'conversionRate', 'rpm']
const TooltipTrendIcon = ({ tone }: { tone: 'positive' | 'negative' | 'neutral' }) => {
  if (tone === 'positive') return <FaArrowTrendUp />
  if (tone === 'negative') return <FaArrowTrendDown />
  return <FaArrowRight />
}
type ChartLegendItem = { id: string; type: 'plainline'; value: string; color: string; payload: { strokeDasharray: string | number } }

export type LinkPerformanceMetric = 'Total clicks' | 'Automated clicks' | 'Commission' | 'Page views' | 'CTR' | 'EPC' | 'Conversions' | 'CR' | 'RPM'
export type LinkPerformanceGroupBy = 'hour' | 'day'

export const linkPerformanceMetrics: LinkPerformanceMetric[] = [
  'Total clicks',
  'Automated clicks',
  'Page views',
  'Commission',
  'CTR',
  'EPC',
  'Conversions',
  'CR',
  'RPM',
]
export const defaultLinkPerformanceMetrics: LinkPerformanceMetric[] = ['Total clicks', 'Automated clicks']
export const linkPerformanceMetricStorageKey = (username?: string) => `chart-settings-link-performance-over-time-${username || ''}`
export const defaultLinkPerformanceGroupBy: LinkPerformanceGroupBy = 'hour'
export const linkPerformanceGroupByStorageKey = (username?: string) => `chart-settings-link-performance-group-by-${username || ''}`

const linkPerformanceMetricDependencies: Partial<Record<LinkPerformanceMetric, LinkPerformanceMetric[]>> = {
  CTR: ['Total clicks', 'Page views'],
  EPC: ['Total clicks', 'Commission'],
  CR: ['Total clicks', 'Conversions'],
  RPM: ['Commission', 'Page views'],
}

export function getLinkPerformanceRequestMetrics(metrics: LinkPerformanceMetric[]) {
  const requested = new Set<LinkPerformanceMetric>()
  for (const metric of metrics) {
    requested.add(metric)
    for (const dependency of linkPerformanceMetricDependencies[metric] || []) requested.add(dependency)
  }
  return Array.from(requested)
}

export function getDerivedLinkPerformanceValues(row: {
  totalClicks?: number | null
  pageViews?: number | null
  commission?: number | null
  conversions?: number | null
}) {
  const totalClicks = Number(row.totalClicks) || 0
  const pageViews = Number(row.pageViews) || 0
  const commission = Number(row.commission) || 0
  const conversions = Number(row.conversions) || 0

  return {
    ctr: pageViews > 0 ? (totalClicks / pageViews) * 100 : 0,
    epc: totalClicks > 0 ? commission / totalClicks : 0,
    conversionRate: totalClicks > 0 ? (conversions / totalClicks) * 100 : 0,
    rpm: pageViews > 0 ? (commission / pageViews) * 1000 : 0,
  }
}

const additionalMetricConfigs = [
  { metric: 'CTR', key: 'ctr', color: ctrLineColor, yAxisId: 'percentage' },
  { metric: 'EPC', key: 'epc', color: epcLineColor, yAxisId: 'commission' },
  { metric: 'Conversions', key: 'conversions', color: conversionsLineColor, yAxisId: 'count' },
  { metric: 'CR', key: 'conversionRate', color: conversionRateLineColor, yAxisId: 'percentage' },
  { metric: 'RPM', key: 'rpm', color: rpmLineColor, yAxisId: 'commission' },
] as const

export function getStoredLinkPerformanceMetrics(username?: string): LinkPerformanceMetric[] | null {
  try {
    const stored = localStorage.getItem(linkPerformanceMetricStorageKey(username))
    if (!stored) return null
    const parsed = JSON.parse(stored)
    if (!Array.isArray(parsed)) return null
    const filtered = parsed.filter((metric): metric is LinkPerformanceMetric => linkPerformanceMetrics.includes(metric))
    return filtered.length ? filtered : null
  } catch {
    return null
  }
}

export function getStoredLinkPerformanceGroupBy(username?: string): LinkPerformanceGroupBy {
  try {
    const stored = localStorage.getItem(linkPerformanceGroupByStorageKey(username))
    return stored === 'day' || stored === 'hour' ? stored : defaultLinkPerformanceGroupBy
  } catch {
    return defaultLinkPerformanceGroupBy
  }
}

const DropdownToggleIconOnly = React.forwardRef<HTMLButtonElement, any>(({ onClick }, ref) => (
  <button
    ref={ref}
    onClick={(e) => {
      e.preventDefault()
      onClick(e)
    }}
    className="btn btn-sm"
    aria-label="Settings"
  >
    <FiSettings />
  </button>
))

function parseHour(value: string) {
  const date = new Date(value)
  return Number.isFinite(date.getTime()) ? date : null
}

function parseDateValue(value: string, endOfDay = false) {
  const date = value.includes('T') ? new Date(value) : new Date(`${value}T00:00:00`)
  if (!Number.isFinite(date.getTime())) return null

  if (endOfDay) date.setHours(23, 0, 0, 0)
  else date.setHours(0, 0, 0, 0)

  return date
}

function getCurrentBucket(groupBy: LinkPerformanceGroupBy) {
  const now = new Date()
  if (groupBy === 'day') {
    now.setHours(0, 0, 0, 0)
    return now
  }

  now.setMinutes(0, 0, 0)
  return now
}

function getCurrentDisplayBucket(groupBy: LinkPerformanceGroupBy) {
  const bucket = getCurrentBucket(groupBy)
  if (groupBy === 'hour') {
    bucket.setHours(bucket.getHours() + 1)
  }
  return bucket
}

function formatHourKey(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hour = String(date.getHours()).padStart(2, '0')
  return `${year}-${month}-${day}T${hour}:00:00`
}

function formatDayKey(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}T00:00:00`
}

function getSelectedBuckets(fromDate: string, toDate: string, groupBy: LinkPerformanceGroupBy) {
  const start = parseDateValue(fromDate)
  const end = parseDateValue(toDate, true)
  if (!start || !end) return []

  const first = start <= end ? start : end
  const last = start <= end ? end : start
  const buckets: string[] = []
  const cursor = new Date(first)
  let guard = 0

  if (groupBy === 'day') {
    cursor.setHours(0, 0, 0, 0)
    last.setHours(0, 0, 0, 0)
    while (cursor <= last && guard < 20000) {
      buckets.push(formatDayKey(cursor))
      cursor.setDate(cursor.getDate() + 1)
      guard += 1
    }
    return buckets
  }

  cursor.setHours(0, 0, 0, 0)
  last.setHours(24, 0, 0, 0)
  while (cursor <= last && guard < 20000) {
    buckets.push(formatHourKey(cursor))
    cursor.setHours(cursor.getHours() + 1)
    guard += 1
  }

  return buckets
}

function getHourlyDisplayBucketKey(hour: string) {
  const date = parseHour(hour)
  if (!date) return hour
  date.setHours(date.getHours() + 1, 0, 0, 0)
  return formatHourKey(date)
}

function isEndOfSingleDayBoundary(value: string, firstValue?: string) {
  const date = parseHour(value)
  const firstDate = parseHour(firstValue || '')
  if (!date || !firstDate) return false

  const expectedBoundary = new Date(firstDate)
  expectedBoundary.setHours(24, 0, 0, 0)

  return date.getTime() === expectedBoundary.getTime()
}

function formatTick(value: string, singleDay: boolean, groupBy: LinkPerformanceGroupBy, firstValue?: string) {
  const date = parseHour(value)
  if (!date) return value

  if (groupBy === 'hour') {
    if (isEndOfSingleDayBoundary(value, firstValue)) return '24:00'

    if (!singleDay && date.getHours() === 0) {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    }

    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}

function formatTooltipLabel(value: string, groupBy: LinkPerformanceGroupBy, firstValue?: string) {
  const date = parseHour(value)
  if (!date) return value

  if (groupBy === 'day') {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  if (groupBy === 'hour') {
    if (value === firstValue) {
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    }

    const startDate = new Date(date)
    startDate.setHours(startDate.getHours() - 1, 0, 0, 0)
    const dateLabel = startDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })
    const startLabel = startDate.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    const endLabel = isEndOfSingleDayBoundary(value, firstValue)
      ? '24:00'
      : date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })

    return `${dateLabel}, ${startLabel} - ${endLabel}`
  }

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

function addHourlyEndBoundary<Row extends { hour: string }>(rows: Row[]) {
  if (rows.length === 0) return rows

  const lastDate = parseHour(rows[rows.length - 1].hour)
  if (!lastDate) return rows

  const boundaryDate = new Date(lastDate)
  boundaryDate.setHours(boundaryDate.getHours() + 1, 0, 0, 0)
  const boundaryHour = formatHourKey(boundaryDate)

  if (rows.some((row) => row.hour === boundaryHour)) return rows

  return [
    ...rows,
    {
      ...rows[rows.length - 1],
      hour: boundaryHour,
      automatedClicks: null,
      totalClicks: null,
      commission: null,
      pageViews: null,
      conversions: null,
      ctr: null,
      epc: null,
      conversionRate: null,
      rpm: null,
    },
  ]
}

function areMetricSetsEqual(first: LinkPerformanceMetric[], second: LinkPerformanceMetric[]) {
  if (first.length !== second.length) return false
  const selected = new Set(first)
  return second.every((metric) => selected.has(metric))
}

const ChartLegend: FC<{ payload: ChartLegendItem[] }> = ({ payload }) => {
  if (!payload.length) return null

  return (
    <div className="link-script-chart-legend">
      {payload.map((item) => {
        const isDashed = Boolean(item.payload.strokeDasharray && item.payload.strokeDasharray !== 0)

        return (
          <div key={item.id} className="link-script-chart-legend-item">
            <span
              className={`link-script-chart-legend-line${isDashed ? ' is-dashed' : ''}`}
              style={{ borderColor: item.color }}
            />
            <span>{item.value}</span>
          </div>
        )
      })}
    </div>
  )
}

const CustomTooltip: FC<any> = ({ active, payload, label, currency, groupBy, firstValue }) => {
  const visiblePayload = (payload || [])
    .filter((entry: any) => entry.value !== null && entry.value !== undefined)
    .filter((entry: any, index: number, entries: any[]) => entries.findIndex((item: any) => item.name === entry.name) === index)
  if (!active || !visiblePayload.length) return null

  const getMetricKey = (entry: any) => String(entry.dataKey || '').replace(/(Compare|Solid|InProgress)$/, '')
  const formatValue = (entry: any) => {
    const value = Number(entry.value) || 0
    const metricKey = getMetricKey(entry)
    if (metricKey === 'commission' || metricKey === 'epc' || metricKey === 'rpm') return formatNumber(value, currency || '')
    if (metricKey === 'ctr' || metricKey === 'conversionRate') return `${formatNumberLocale(value, 2)}%`
    return formatNumberLocale(value, 0)
  }
  const getChange = (current: any, previous: any) => {
    if (!current || !previous) return null

    const currentValue = Number(current.value) || 0
    const previousValue = Number(previous.value) || 0

    if (previousValue === 0) {
      if (currentValue === 0) return { label: '0%', tone: 'neutral' }
      return { label: 'New', tone: 'positive' }
    }

    const change = ((currentValue - previousValue) / Math.abs(previousValue)) * 100
    const tone = change > 0 ? 'positive' : change < 0 ? 'negative' : 'neutral'

    return {
      label: `${change > 0 ? '+' : ''}${formatNumberLocale(change, 1)}%`,
      tone,
    }
  }
  const metricGroups = tooltipMetricOrder
    .map((metricKey) => {
      const current = visiblePayload.find((entry: any) => getMetricKey(entry) === metricKey && !String(entry.dataKey || '').endsWith('Compare'))
      const previous = visiblePayload.find((entry: any) => getMetricKey(entry) === metricKey && String(entry.dataKey || '').endsWith('Compare'))

      if (!current && !previous) return null

      return {
        metricKey,
        current,
        previous,
        label: current?.name || String(previous?.name || '').replace(/^Previous\s+/i, ''),
        change: getChange(current, previous),
      }
    })
    .filter(Boolean)

  return (
    <div className="bg-white border shadow-sm px-3 py-2 link-script-compare-tooltip">
      <div className="fw-semibold mb-1">{formatTooltipLabel(label, groupBy, firstValue)}</div>
      {metricGroups.map((group: any) => (
        <div key={group.metricKey} className="link-script-compare-tooltip-group">
          {group.current && (
            <div className="link-script-compare-tooltip-row">
              <span className="link-script-compare-tooltip-dot" style={{ backgroundColor: group.current.color }} />
              <span className="link-script-compare-tooltip-name">{group.label}</span>
              {group.change && (
                <span className={`link-script-compare-tooltip-change is-${group.change.tone}`}>
                  <TooltipTrendIcon tone={group.change.tone} />
                  {group.change.label}
                </span>
              )}
              <span className="link-script-compare-tooltip-value">{formatValue(group.current)}</span>
            </div>
          )}
          {group.previous && (
            <div className="link-script-compare-tooltip-row is-previous">
              <span className="link-script-compare-tooltip-dot is-previous" style={{ borderColor: group.previous.color }} />
              <span className="link-script-compare-tooltip-name">Previous</span>
              <span className="link-script-compare-tooltip-value is-previous">{formatValue(group.previous)}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function ChartSettingsDropdown(opts: {
  selectedMetrics: LinkPerformanceMetric[]
  selectedGroupBy: LinkPerformanceGroupBy
  groupByOptions: LinkPerformanceGroupBy[]
  onSubmit: (metrics: LinkPerformanceMetric[], groupBy: LinkPerformanceGroupBy) => void
}) {
  const { selectedMetrics, selectedGroupBy, groupByOptions, onSubmit } = opts
  const [show, setShow] = useState(false)
  const [internalSelection, setInternalSelection] = useState<LinkPerformanceMetric[]>(selectedMetrics)
  const [internalGroupBy, setInternalGroupBy] = useState<LinkPerformanceGroupBy>(selectedGroupBy)
  const effectiveSelectedGroupBy = groupByOptions.includes(selectedGroupBy) ? selectedGroupBy : groupByOptions[0]
  const effectiveInternalGroupBy = groupByOptions.includes(internalGroupBy) ? internalGroupBy : effectiveSelectedGroupBy

  useEffect(() => {
    setInternalSelection(selectedMetrics)
    setInternalGroupBy(effectiveSelectedGroupBy)
  }, [selectedMetrics, effectiveSelectedGroupBy, show])

  const toggleMetric = (metric: LinkPerformanceMetric) => {
    setInternalSelection((current) => {
      if (current.includes(metric)) {
        const next = current.filter((item) => item !== metric)
        return next.length ? next : current
      }
      return [...current, metric]
    })
  }

  const submit = () => {
    setShow(false)
    onSubmit(internalSelection, effectiveInternalGroupBy)
  }

  return (
    <Dropdown align="end" show={show} onToggle={(isOpen: boolean) => setShow(isOpen)}>
      <Dropdown.Toggle as={DropdownToggleIconOnly} id="link-performance-chart-settings" />
      <Dropdown.Menu className="p-2" style={{ minWidth: '260px' }}>
        <strong className="dropdown-header">Show metrics</strong>
        <div className="px-2 d-flex flex-column">
          {linkPerformanceMetrics.map((metric) => (
            <Form.Check
              key={metric}
              type="checkbox"
              id={`toggle-link-performance-${metric}`}
              label={metric}
              checked={internalSelection.includes(metric)}
              onChange={() => toggleMetric(metric)}
              className="py-0"
            />
          ))}
        </div>
        <strong className="dropdown-header mt-2">Group by</strong>
        <div className="px-2 d-flex gap-3">
          {(['hour', 'day'] as LinkPerformanceGroupBy[]).map((groupBy) => (
            <Form.Check
              key={groupBy}
              type="radio"
              id={`toggle-link-performance-group-${groupBy}`}
              name="link-performance-group-by"
              label={groupBy === 'hour' ? 'Hour' : 'Day'}
              checked={effectiveInternalGroupBy === groupBy}
              disabled={!groupByOptions.includes(groupBy)}
              onChange={() => setInternalGroupBy(groupBy)}
              className="py-1"
            />
          ))}
        </div>
        <div className="d-flex justify-content-end mt-2 px-2">
          <Button variant="primary" size="sm" onClick={submit}>
            Submit
          </Button>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  )
}

const HourlyClicksChart: FC<{
  rows: HourlyClickRow[]
  compareRows?: HourlyClickRow[]
  currency: string
  fromDate: string
  toDate: string
  compareFromDate?: string
  compareToDate?: string
  title?: string
  username?: string
  selectedMetrics: LinkPerformanceMetric[]
  selectedGroupBy: LinkPerformanceGroupBy
  groupByOptions: LinkPerformanceGroupBy[]
  onSelectedSettingsChange: (metrics: LinkPerformanceMetric[], groupBy: LinkPerformanceGroupBy) => void
}> = ({
  rows,
  compareRows = [],
  currency,
  fromDate,
  toDate,
  compareFromDate,
  compareToDate,
  title = 'Performance over time',
  selectedMetrics,
  selectedGroupBy,
  groupByOptions,
  onSelectedSettingsChange,
}) => {
  const chartData = useMemo(() => {
    const currentBucket = getCurrentDisplayBucket(selectedGroupBy)
    const currentBucketKey = selectedGroupBy === 'day' ? formatDayKey(currentBucket) : formatHourKey(currentBucket)

    const normalizeRows = (sourceRows: HourlyClickRow[], sourceFromDate: string, sourceToDate: string) => {
      const rowByBucket = new Map(sourceRows.map((row) => [
        selectedGroupBy === 'hour' ? getHourlyDisplayBucketKey(row.hour) : row.hour,
        row,
      ]))
      const selectedBuckets = getSelectedBuckets(sourceFromDate, sourceToDate, selectedGroupBy)
      const buckets = selectedBuckets.length ? selectedBuckets : sourceRows.map((row) => row.hour)

      return {
        selectedBuckets,
        rows: buckets.map((hour) => {
          const row = rowByBucket.get(hour)
          const totalClicks = Number(row?.totalClicks) || 0
          const pageViews = Number(row?.pageViews) || 0
          const commission = Number(row?.commission) || 0
          const conversions = Number(row?.conversions) || 0
          const derivedValues = getDerivedLinkPerformanceValues({ totalClicks, pageViews, commission, conversions })
          return {
            hour,
            automatedClicks: Number(row?.automatedClicks) || 0,
            totalClicks,
            commission,
            pageViews,
            conversions,
            ...derivedValues,
          }
        }),
      }
    }

    const normalizedCurrent = normalizeRows(rows, fromDate, toDate)
    const normalizedRows = normalizedCurrent.rows
      .map((row) => {
        const rowBucket = parseHour(row.hour)
        if (!rowBucket || rowBucket <= currentBucket) return row

        return {
          ...row,
          automatedClicks: null,
          totalClicks: null,
          commission: null,
          pageViews: null,
          conversions: null,
          ctr: null,
          epc: null,
          conversionRate: null,
          rpm: null,
        }
      })
      .sort((first, second) => first.hour.localeCompare(second.hour))

    const rowsWithBoundary = selectedGroupBy === 'hour' && !normalizedCurrent.selectedBuckets.length ? addHourlyEndBoundary(normalizedRows) : normalizedRows
    const currentBucketIndex = rowsWithBoundary.findIndex((row) => row.hour === currentBucketKey)
    const normalizedCompareRows = compareRows.length && compareFromDate && compareToDate
      ? normalizeRows(compareRows, compareFromDate, compareToDate).rows
          .sort((first, second) => first.hour.localeCompare(second.hour))
      : []

    return rowsWithBoundary.map((row, index) => {
      const decoratedRow: any = { ...row }
      const hasInProgressSegment = selectedGroupBy === 'hour' && currentBucketIndex > 0
      const isCurrentBucket = index === currentBucketIndex
      const isInProgressPoint = hasInProgressSegment && (index === currentBucketIndex || index === currentBucketIndex - 1)

      metricValueKeys.forEach((key) => {
        decoratedRow[`${key}Solid`] = hasInProgressSegment && isCurrentBucket ? null : row[key]
        decoratedRow[`${key}InProgress`] = isInProgressPoint ? row[key] : null
      })

      const compareRow = normalizedCompareRows[index]
      metricValueKeys.forEach((key) => {
        decoratedRow[`${key}Compare`] = compareRow ? compareRow[key] : null
      })

      return decoratedRow
    })
  }, [compareFromDate, compareRows, compareToDate, fromDate, rows, selectedGroupBy, toDate])

  const singleDay = useMemo(() => {
    const first = parseHour(chartData[0]?.hour || '')
    const last = parseHour(chartData[chartData.length - 1]?.hour || '')
    if (!first || !last) return true

    if (selectedGroupBy === 'hour' && isEndOfSingleDayBoundary(chartData[chartData.length - 1]?.hour || '', chartData[0]?.hour)) {
      return true
    }

    return first.toDateString() === last.toDateString()
  }, [chartData, selectedGroupBy])

  const xAxisTicks = useMemo(() => {
    if (selectedGroupBy !== 'hour') return undefined
    const first = parseHour(chartData[0]?.hour || '')
    const last = parseHour(chartData[chartData.length - 1]?.hour || '')
    if (!first || !last) return undefined

    const hourSpan = Math.max(1, Math.round((last.getTime() - first.getTime()) / (60 * 60 * 1000)))
    const hourStep = singleDay ? 2 : (hourSpan <= 48 ? 4 : hourSpan <= 96 ? 6 : 12)

    return chartData
      .filter((row) => {
        const date = parseHour(row.hour)
        if (!date) return false
        return date.getHours() === 0 || date.getHours() % hourStep === 0 || isEndOfSingleDayBoundary(row.hour, chartData[0]?.hour)
      })
      .map((row) => row.hour)
  }, [chartData, selectedGroupBy, singleDay])

  const legendPayload = useMemo(() => {
    const currentPayload: ChartLegendItem[] = []
    const previousPayload: ChartLegendItem[] = []
    if (selectedMetrics.includes('Total clicks')) {
      currentPayload.push({ id: 'totalClicks', type: 'plainline', value: 'Total clicks', color: dashboardTableBlue, payload: { strokeDasharray: 0 } })
      if (compareRows.length) previousPayload.push({ id: 'totalClicksCompare', type: 'plainline', value: 'Previous total clicks', color: dashboardTableBlue, payload: { strokeDasharray: '5 5' } })
    }
    if (selectedMetrics.includes('Automated clicks')) {
      currentPayload.push({ id: 'automatedClicks', type: 'plainline', value: 'Automated clicks', color: automatedLineColor, payload: { strokeDasharray: 0 } })
      if (compareRows.length) previousPayload.push({ id: 'automatedClicksCompare', type: 'plainline', value: 'Previous automated clicks', color: automatedLineColor, payload: { strokeDasharray: '5 5' } })
    }
    if (selectedMetrics.includes('Page views')) {
      currentPayload.push({ id: 'pageViews', type: 'plainline', value: 'Page views', color: pageViewsLineColor, payload: { strokeDasharray: 0 } })
      if (compareRows.length) previousPayload.push({ id: 'pageViewsCompare', type: 'plainline', value: 'Previous page views', color: pageViewsLineColor, payload: { strokeDasharray: '5 5' } })
    }
    if (selectedMetrics.includes('Commission')) {
      currentPayload.push({ id: 'commission', type: 'plainline', value: 'Commission', color: commissionLineColor, payload: { strokeDasharray: 0 } })
      if (compareRows.length) previousPayload.push({ id: 'commissionCompare', type: 'plainline', value: 'Previous commission', color: commissionLineColor, payload: { strokeDasharray: '5 5' } })
    }
    for (const config of additionalMetricConfigs) {
      if (!selectedMetrics.includes(config.metric)) continue
      currentPayload.push({ id: config.key, type: 'plainline', value: config.metric, color: config.color, payload: { strokeDasharray: 0 } })
      if (compareRows.length) {
        previousPayload.push({ id: `${config.key}Compare`, type: 'plainline', value: `Previous ${config.metric.toLowerCase()}`, color: config.color, payload: { strokeDasharray: '5 5' } })
      }
    }
    return [...currentPayload, ...previousPayload]
  }, [compareRows.length, selectedMetrics])

  const updateChartSettings = (metrics: LinkPerformanceMetric[], groupBy: LinkPerformanceGroupBy) => {
    if (!areMetricSetsEqual(metrics, selectedMetrics) || groupBy !== selectedGroupBy) {
      onSelectedSettingsChange(metrics, groupBy)
    }
  }

  if (!chartData.length) return null

  return (
    <div className="mb-4">
      <div className="mb-2">
        <div className="d-flex align-items-center justify-content-between gap-2">
          <h6 className="mb-2 fw-bold text-uppercase" style={{ color: dashboardTableBlue, fontSize: '0.95rem', letterSpacing: '0.02em' }}>
            {title}
          </h6>
          <div className="mb-2">
            <ChartSettingsDropdown
              selectedMetrics={selectedMetrics}
              selectedGroupBy={selectedGroupBy}
              groupByOptions={groupByOptions}
              onSubmit={updateChartSettings}
            />
          </div>
        </div>
        <div style={{ height: 1, backgroundColor: 'rgba(91, 155, 213, 0.35)' }} />
      </div>

      <ChartLegend payload={legendPayload} />

      <div style={{ width: '100%', height: 340 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,.08)" />
            <XAxis
              dataKey="hour"
              tick={{ fontSize: 11 }}
              tickFormatter={(value) => formatTick(String(value), singleDay, selectedGroupBy, chartData[0]?.hour)}
              ticks={xAxisTicks}
              minTickGap={24}
            />
            <YAxis yAxisId="count" tick={{ fontSize: 11 }} tickFormatter={(value) => formatNumberLocale(Number(value), 0)} width={48} />
            {selectedMetrics.some((metric) => ['Commission', 'EPC', 'RPM'].includes(metric)) && (
              <YAxis
                yAxisId="commission"
                orientation="right"
                tick={{ fontSize: 11 }}
                tickFormatter={(value) => formatNumber(Number(value), currency)}
                width={68}
              />
            )}
            {selectedMetrics.some((metric) => ['CTR', 'CR'].includes(metric)) && (
              <YAxis
                yAxisId="percentage"
                orientation="right"
                tick={{ fontSize: 11 }}
                tickFormatter={(value) => `${formatNumberLocale(Number(value), 1)}%`}
                width={60}
              />
            )}
            <Tooltip content={<CustomTooltip currency={currency} groupBy={selectedGroupBy} firstValue={chartData[0]?.hour} />} />
            {selectedMetrics.includes('Total clicks') && (
              <>
                {compareRows.length > 0 && (
                  <Line
                    yAxisId="count"
                    type="monotone"
                    dataKey="totalClicksCompare"
                    name="Previous total clicks"
                    stroke={dashboardTableBlue}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    strokeOpacity={0.6}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                )}
                <Line
                  yAxisId="count"
                  type="monotone"
                  dataKey="totalClicksSolid"
                  name="Total clicks"
                  stroke={dashboardTableBlue}
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
                <Line
                  yAxisId="count"
                  type="monotone"
                  dataKey="totalClicksInProgress"
                  name="Total clicks"
                  stroke={dashboardTableBlue}
                  strokeWidth={2.5}
                  strokeDasharray="4 4"
                  strokeOpacity={inProgressLineOpacity}
                  dot={false}
                  activeDot={{ r: 4 }}
                  legendType="none"
                />
              </>
            )}
            {selectedMetrics.includes('Automated clicks') && (
              <>
                {compareRows.length > 0 && (
                  <Line
                    yAxisId="count"
                    type="monotone"
                    dataKey="automatedClicksCompare"
                    name="Previous automated clicks"
                    stroke={automatedLineColor}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    strokeOpacity={0.6}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                )}
                <Line
                  yAxisId="count"
                  type="monotone"
                  dataKey="automatedClicksSolid"
                  name="Automated clicks"
                  stroke={automatedLineColor}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
                <Line
                  yAxisId="count"
                  type="monotone"
                  dataKey="automatedClicksInProgress"
                  name="Automated clicks"
                  stroke={automatedLineColor}
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  strokeOpacity={inProgressLineOpacity}
                  dot={false}
                  activeDot={{ r: 4 }}
                  legendType="none"
                />
              </>
            )}
            {selectedMetrics.includes('Page views') && (
              <>
                {compareRows.length > 0 && (
                  <Line
                    yAxisId="count"
                    type="monotone"
                    dataKey="pageViewsCompare"
                    name="Previous page views"
                    stroke={pageViewsLineColor}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    strokeOpacity={0.6}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                )}
                <Line
                  yAxisId="count"
                  type="monotone"
                  dataKey="pageViewsSolid"
                  name="Page views"
                  stroke={pageViewsLineColor}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
                <Line
                  yAxisId="count"
                  type="monotone"
                  dataKey="pageViewsInProgress"
                  name="Page views"
                  stroke={pageViewsLineColor}
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  strokeOpacity={inProgressLineOpacity}
                  dot={false}
                  activeDot={{ r: 4 }}
                  legendType="none"
                />
              </>
            )}
            {selectedMetrics.includes('Commission') && (
              <>
                {compareRows.length > 0 && (
                  <Line
                    yAxisId="commission"
                    type="monotone"
                    dataKey="commissionCompare"
                    name="Previous commission"
                    stroke={commissionLineColor}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    strokeOpacity={0.6}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                )}
                <Line
                  yAxisId="commission"
                  type="monotone"
                  dataKey="commissionSolid"
                  name="Commission"
                  stroke={commissionLineColor}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
                <Line
                  yAxisId="commission"
                  type="monotone"
                  dataKey="commissionInProgress"
                  name="Commission"
                  stroke={commissionLineColor}
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  strokeOpacity={inProgressLineOpacity}
                  dot={false}
                  activeDot={{ r: 4 }}
                  legendType="none"
                />
              </>
            )}
            {additionalMetricConfigs.map((config) => selectedMetrics.includes(config.metric) ? (
              <React.Fragment key={config.key}>
                {compareRows.length > 0 && (
                  <Line
                    yAxisId={config.yAxisId}
                    type="monotone"
                    dataKey={`${config.key}Compare`}
                    name={`Previous ${config.metric.toLowerCase()}`}
                    stroke={config.color}
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    strokeOpacity={0.6}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                )}
                <Line
                  yAxisId={config.yAxisId}
                  type="monotone"
                  dataKey={`${config.key}Solid`}
                  name={config.metric}
                  stroke={config.color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
                <Line
                  yAxisId={config.yAxisId}
                  type="monotone"
                  dataKey={`${config.key}InProgress`}
                  name={config.metric}
                  stroke={config.color}
                  strokeWidth={2}
                  strokeDasharray="4 4"
                  strokeOpacity={inProgressLineOpacity}
                  dot={false}
                  activeDot={{ r: 4 }}
                  legendType="none"
                />
              </React.Fragment>
            ) : null)}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

export default HourlyClicksChart
